/**
 * Biometric Verification Panel
 * ============================
 * World-class Persona-like biometric verification results display.
 *
 * Features:
 * - Overview with summary stats and document gallery
 * - Verification checks table with Passed/Failed badges
 * - Selfie comparison view
 * - Session/device info with signals
 * - Reports section (PEP, Watchlist, Adverse Media)
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Camera,
  FileText,
  Shield,
  Wifi,
  Monitor,
  Globe,
  Smartphone,
  MapPin,
  Eye,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Fingerprint,
  Scan,
  User,
  Zap,
  Activity,
  Info,
  ShieldAlert,
  Radio,
  Server,
  Bug,
  Bot,
  Network,
  Navigation,
} from 'lucide-react';

// Parse user agent string into structured components
function parseUserAgent(ua) {
  if (!ua) return { browser: '\u2014', os: '\u2014', device: '\u2014', full: ua || '' };
  let browser = '\u2014', os = '\u2014', device = '\u2014';

  // Browser detection
  if (ua.includes('CriOS')) {
    const m = ua.match(/CriOS\/([\d.]+)/);
    browser = `Chrome iOS ${m ? m[1].split('.')[0] : ''}`;
  } else if (ua.includes('Edg/')) {
    const m = ua.match(/Edg\/([\d.]+)/);
    browser = `Edge ${m ? m[1].split('.')[0] : ''}`;
  } else if (ua.includes('Chrome/') && !ua.includes('Chromium')) {
    const m = ua.match(/Chrome\/([\d.]+)/);
    browser = `Chrome ${m ? m[1].split('.')[0] : ''}`;
  } else if (ua.includes('Firefox/')) {
    const m = ua.match(/Firefox\/([\d.]+)/);
    browser = `Firefox ${m ? m[1].split('.')[0] : ''}`;
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const m = ua.match(/Version\/([\d.]+)/);
    browser = `Safari ${m ? m[1] : ''}`;
  }

  // OS detection
  if (ua.includes('Android')) {
    const m = ua.match(/Android\s([\d.]+)/);
    os = `Android ${m ? m[1] : ''}`;
  } else if (ua.includes('iPhone OS') || ua.includes('iPad')) {
    const m = ua.match(/OS\s([\d_]+)/);
    os = `iOS ${m ? m[1].replace(/_/g, '.') : ''}`;
  } else if (ua.includes('Windows NT')) {
    const versions = { '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7' };
    const m = ua.match(/Windows NT\s([\d.]+)/);
    os = `Windows ${m ? (versions[m[1]] || m[1]) : ''}`;
  } else if (ua.includes('Mac OS X')) {
    const m = ua.match(/Mac OS X\s([\d_.]+)/);
    os = `macOS ${m ? m[1].replace(/_/g, '.') : ''}`;
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  // Device detection (from UA model strings)
  const deviceMatch = ua.match(/;\s*([^;)]+)\s*Build\//);
  if (deviceMatch) {
    device = deviceMatch[1].trim();
  } else if (ua.includes('iPhone')) {
    device = 'iPhone';
  } else if (ua.includes('iPad')) {
    device = 'iPad';
  } else if (ua.includes('Macintosh')) {
    device = 'Mac';
  }

  return { browser: browser.trim(), os: os.trim(), device, full: ua };
}

// Check explanation tooltips
const CHECK_EXPLANATIONS = {
  'Selfie-to-ID Comparison': 'Confirms the selfie matches the face on the submitted ID',
  'Liveness Detection': 'Verifies the person is physically present during verification',
  'Anti-Spoof Score': 'Measures whether the image is a real face vs a photo/screen replay',
  'Portrait Quality': 'Checks image clarity, lighting, and sharpness for reliable analysis',
  'Eyes Open': 'Ensures the face is alert and not obstructed',
  'Acceptable Pose': 'Verifies face angle is within analyzable range',
  'Image Brightness': 'Checks lighting level is sufficient for reliable face analysis',
  'Image Sharpness': 'Ensures image is in focus for accurate feature extraction',
  'Multiple Faces': 'Verifies only one person is present in the capture',
  'Age Consistency': 'Cross-references estimated age with date of birth on ID',
  'Frame Consistency': 'Measures how stable anti-spoof scores are across captured frames',
  'Government ID Detected': 'Confirms a valid government-issued identity document was submitted',
  'Not Expired': 'Verifies the document has not passed its expiration date',
  'MRZ Validated': 'Machine-readable zone data is consistent and properly formatted',
  'No Blur': 'Checks that the document image is clear and readable',
  'No Glare': 'Ensures no light reflection obscures document details',
  'Name Consistency': 'Cross-references the name on the ID with the submitted name',
  'OCR Confidence': 'How reliably text was extracted from the document image',
  'Document Integrity': 'Checks for signs of digital tampering or fabrication',
  'Network Threat Level': 'Assesses risk signals from the user\'s network connection',
  'Proxy/VPN Detected': 'Flags connections routed through anonymizing services',
  'Abuse Velocity': 'Tracks how frequently this IP has been associated with fraud',
  'Address Deliverability': 'Confirms the submitted address can receive mail',
  'High-Risk Jurisdiction': 'Flags addresses in FATF grey/blacklisted countries',
  'Phone Line Type': 'Identifies if the phone is mobile, landline, or VoIP',
};

// Sub-tabs for the biometric panel
const TABS = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'verifications', label: 'Verifications', icon: CheckCircle2 },
  { id: 'sessions', label: 'Sessions', icon: Monitor },
  { id: 'signals', label: 'Signals', icon: Zap },
];

// Status badge component
const StatusBadge = ({ status, size = 'md' }) => {
  const config = {
    passed: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', icon: CheckCircle2, label: 'Passed' },
    failed: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', icon: XCircle, label: 'Failed' },
    pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', icon: Clock, label: 'Pending' },
    review: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', icon: AlertCircle, label: 'Review' },
    clear: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981', icon: CheckCircle2, label: 'Clear' },
    hit: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', icon: AlertTriangle, label: 'Hit' },
    not_run: { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af', icon: Clock, label: 'Not Run' },
  };

  const c = config[status?.toLowerCase()] || config.pending;
  const Icon = c.icon;
  const sizeStyles = size === 'sm'
    ? { padding: '3px 8px', fontSize: '11px', gap: '4px' }
    : { padding: '5px 10px', fontSize: '12px', gap: '5px' };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      background: c.bg,
      color: c.color,
      borderRadius: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      ...sizeStyles,
    }}>
      <Icon size={size === 'sm' ? 10 : 12} />
      {c.label}
    </span>
  );
};

// Confidence meter component (used for displaying score bars)
// eslint-disable-next-line no-unused-vars
const ConfidenceMeter = ({ value, label, showPercentage = true }) => {
  const getColor = (v) => {
    if (v >= 90) return '#10b981';
    if (v >= 70) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div style={{ marginBottom: '12px' }}>
      {label && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '6px',
          fontSize: '13px',
          color: 'var(--text-secondary)',
        }}>
          <span>{label}</span>
          {showPercentage && <span style={{ fontWeight: 600, color: getColor(value) }}>{value}%</span>}
        </div>
      )}
      <div style={{
        height: '6px',
        background: 'var(--bg-tertiary)',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${value}%`,
          background: getColor(value),
          borderRadius: '3px',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};

// Document image card
const DocumentCard = ({ title, imageUrl, status, side }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset error state when imageUrl changes
  React.useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const showImage = imageUrl && !imageError;

  return (
    <div style={{
      background: 'var(--bg-tertiary)',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{
        padding: '12px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '13px', fontWeight: 500 }}>{title}</span>
          {side && (
            <span style={{
              fontSize: '10px',
              padding: '2px 6px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
            }}>
              {side}
            </span>
          )}
        </div>
        <StatusBadge status={status} size="sm" />
      </div>
      <div
        style={{
          position: 'relative',
          height: expanded ? '300px' : '160px',
          background: '#1a1a2e',
          cursor: 'pointer',
          transition: 'height 0.2s ease',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {showImage ? (
          <img
            src={imageUrl}
            alt={title}
            onError={() => setImageError(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: '8px',
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <Camera size={32} style={{ opacity: 0.4 }} />
            <span style={{ fontSize: '12px' }}>
              {imageError ? 'Image unavailable' : status === 'pending' ? 'Awaiting upload' : 'Processing...'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Face comparison image with error handling
const FaceImage = ({ src, alt, icon: Icon }) => {
  const [error, setError] = useState(false);

  React.useEffect(() => {
    setError(false);
  }, [src]);

  if (!src || error) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={32} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setError(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

// Face comparison view
const FaceComparisonView = ({ idPhoto, selfie, similarity, matched }) => {
  return (
    <div style={{
      background: 'var(--bg-tertiary)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Fingerprint size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Face Comparison</span>
        </div>
        <StatusBadge status={matched === true ? 'passed' : matched === false ? 'failed' : 'pending'} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: '16px',
        alignItems: 'center',
      }}>
        {/* ID Photo */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '120px',
            height: '150px',
            background: '#1a1a2e',
            borderRadius: '12px',
            margin: '0 auto 8px',
            overflow: 'hidden',
            border: '2px solid var(--border-color)',
          }}>
            <FaceImage src={idPhoto} alt="ID document" icon={FileText} />
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ID Document</span>
        </div>

        {/* Match indicator */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: matched === true ? 'rgba(16, 185, 129, 0.15)' : matched === false ? 'rgba(239, 68, 68, 0.15)' : 'rgba(156, 163, 175, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${matched === true ? '#10b981' : matched === false ? '#ef4444' : '#9ca3af'}`,
          }}>
            {matched === true ? (
              <CheckCircle2 size={28} color="#10b981" />
            ) : matched === false ? (
              <XCircle size={28} color="#ef4444" />
            ) : (
              <Clock size={28} color="#9ca3af" />
            )}
          </div>
          <span style={{
            fontSize: '20px',
            fontWeight: 700,
            color: matched ? '#10b981' : similarity != null ? '#ef4444' : 'var(--text-muted)',
          }}>
            {similarity != null ? `${similarity.toFixed(1)}%` : '---'}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Similarity</span>
        </div>

        {/* Selfie */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '120px',
            height: '150px',
            background: '#1a1a2e',
            borderRadius: '12px',
            margin: '0 auto 8px',
            overflow: 'hidden',
            border: '2px solid var(--border-color)',
          }}>
            <FaceImage src={selfie} alt="Selfie" icon={Camera} />
          </div>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Live Selfie</span>
        </div>
      </div>
    </div>
  );
};

// Helper to format document type for display
const formatDocType = (docType) => {
  if (!docType) return 'Unknown';
  // Handle selfie types
  if (docType.toLowerCase().includes('selfie') || docType.toLowerCase() === 'liveness') {
    return 'Selfie';
  }
  // Capitalize first letter and replace underscores
  return docType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Verification checks table with grouping by source document
const VerificationChecksTable = ({ checks = [] }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (idx) => {
    const next = new Set(expandedRows);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    setExpandedRows(next);
  };

  if (checks.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        padding: '32px',
        textAlign: 'center',
      }}>
        <Clock size={24} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
        <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No verification checks have been run yet</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Checks will appear here after the applicant submits their verification</div>
      </div>
    );
  }

  // Filter out fraud checks incorrectly run on selfies (legacy data before fix)
  const selfieTypes = ['selfie', 'selfie_front', 'selfie_back', 'liveness_selfie', 'photo_selfie', 'liveness'];
  const displayChecks = checks.filter(check => {
    const sourceType = (check.source_document_type || '').toLowerCase();
    const isSelfieSource = selfieTypes.some(t => sourceType.includes(t));
    const isFraudCheck = check.type === 'fraud' || ['ela_forensics', 'synthetic_detection', 'blur_detection', 'tampering_detection'].includes(check.check || check.name);
    // Filter out fraud checks on selfies (they shouldn't exist)
    if (isSelfieSource && isFraudCheck) {
      return false;
    }
    return true;
  });

  // Group checks by source document type for better organization
  const groupedBySource = displayChecks.reduce((acc, check) => {
    const source = check.source_document_type || 'general';
    if (!acc[source]) acc[source] = [];
    acc[source].push(check);
    return acc;
  }, {});

  const sourceOrder = ['passport', 'drivers_license', 'id_card', 'general', 'selfie', 'liveness'];
  const sortedSources = Object.keys(groupedBySource).sort((a, b) => {
    const aIdx = sourceOrder.findIndex(s => a.toLowerCase().includes(s));
    const bIdx = sourceOrder.findIndex(s => b.toLowerCase().includes(s));
    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
  });

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 80px 70px 80px 40px 90px',
        padding: '12px 16px',
        background: 'var(--bg-tertiary)',
        borderBottom: '1px solid var(--border-color)',
        fontSize: '11px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--text-secondary)',
      }}>
        <span>Check</span>
        <span>Source</span>
        <span>Category</span>
        <span>Type</span>
        <span>Confidence</span>
        <span>Req</span>
        <span style={{ textAlign: 'right' }}>Status</span>
      </div>

      {/* Rows grouped by source */}
      {sortedSources.map((source) => {
        const sourceChecks = groupedBySource[source];
        return sourceChecks.map((check, idx) => {
          const globalIdx = displayChecks.indexOf(check);
          return (
            <div key={globalIdx}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 90px 80px 70px 80px 40px 90px',
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onClick={() => toggleRow(globalIdx)}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {expandedRows.has(globalIdx) ? (
                    <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                  ) : (
                    <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                  )}
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{check.name}</span>
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '3px 8px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  justifySelf: 'start',
                }}>
                  {formatDocType(check.source_document_type)}
                </span>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '3px 8px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  color: 'var(--text-primary)',
                  justifySelf: 'start',
                }}>
                  {check.category || 'general'}
                </span>
                <span style={{
                  fontSize: '11px',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  background: check.type === 'fraud' ? 'rgba(239, 68, 68, 0.18)' : 'rgba(59, 130, 246, 0.18)',
                  color: check.type === 'fraud' ? '#fca5a5' : '#93c5fd',
                  fontWeight: 700,
                  justifySelf: 'start',
                }}>
                  {check.type === 'fraud' ? 'Fraud' : 'Info'}
                </span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {check.confidence != null ? `${check.confidence.toFixed(0)}%` : '---'}
                </span>
                <span style={{ textAlign: 'center' }}>
                  {check.required && <CheckCircle2 size={14} style={{ color: 'var(--success)' }} />}
                </span>
                <div style={{ textAlign: 'right' }}>
                  <StatusBadge status={check.status} size="sm" />
                </div>
              </div>

              {/* Expanded details */}
              {expandedRows.has(globalIdx) && (
                <div style={{
                  padding: '12px 16px 12px 40px',
                  background: 'var(--bg-tertiary)',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <p style={{
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: 'var(--text-primary)',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <Info size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                    {check.details || 'No additional details available'}
                  </p>
                </div>
              )}
            </div>
          );
        });
      })}
    </div>
  );
};

// Session/Device info component
const SessionInfo = ({ session = {}, deviceIntel = null }) => {
  const d = deviceIntel || {};
  const hasData = deviceIntel && Object.keys(d).length > 0;

  if (!hasData) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        padding: '32px',
        textAlign: 'center',
      }}>
        <Clock size={24} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
        <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Session data not yet collected</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Device, network, and location data will appear after verification submission</div>
      </div>
    );
  }

  const fraudScore = d.fraud_score ?? 0;
  const riskLevel = d.risk_level || 'unknown';
  const scoreColor = fraudScore >= 75 ? '#ef4444' : fraudScore >= 40 ? '#f59e0b' : '#10b981';
  const riskBadgeColor = riskLevel === 'high' ? '#ef4444' : riskLevel === 'medium' ? '#f59e0b' : '#10b981';

  // Threat flags for the grid
  const threats = [
    { label: 'VPN', active: d.is_vpn, detail: d.active_vpn ? 'Active' : null, icon: Shield },
    { label: 'Proxy', active: d.is_proxy, icon: Network },
    { label: 'Tor', active: d.is_tor, detail: d.active_tor ? 'Active' : null, icon: Eye },
    { label: 'Bot', active: d.is_bot, icon: Bot },
    { label: 'Crawler', active: d.is_crawler, icon: Bug },
    { label: 'Datacenter', active: d.is_datacenter, icon: Server },
    { label: 'Recent Abuse', active: d.recent_abuse, icon: AlertTriangle },
    { label: 'High Risk', active: d.high_risk_attacks, icon: ShieldAlert },
  ];

  const ThreatBadge = ({ threat }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 12px',
      borderRadius: '8px',
      background: threat.active ? 'rgba(239, 68, 68, 0.08)' : 'var(--bg-tertiary)',
      border: `1px solid ${threat.active ? 'rgba(239, 68, 68, 0.2)' : 'var(--border-color)'}`,
    }}>
      <threat.icon size={14} style={{ color: threat.active ? '#ef4444' : 'var(--text-muted)' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '12px', fontWeight: 500, color: threat.active ? '#ef4444' : 'var(--text-primary)' }}>
          {threat.label}
        </div>
        {threat.detail && threat.active && (
          <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '1px' }}>{threat.detail}</div>
        )}
      </div>
      <div style={{
        width: '8px', height: '8px', borderRadius: '50%',
        background: threat.active ? '#ef4444' : '#10b981',
      }} />
    </div>
  );

  const velocityColors = { high: '#ef4444', medium: '#f59e0b', low: '#3b82f6', none: '#10b981' };
  const velocityLevel = (d.abuse_velocity || 'none').toLowerCase();

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {/* Section 1: Risk Overview Banner */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        flexWrap: 'wrap',
      }}>
        {/* Fraud Score Gauge */}
        <div style={{ textAlign: 'center', minWidth: '80px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            border: `4px solid ${scoreColor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${scoreColor}10`,
          }}>
            <span style={{ fontSize: '22px', fontWeight: 700, color: scoreColor }}>{fraudScore}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>Fraud Score</div>
        </div>

        {/* Risk Level */}
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Risk Level</div>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600,
            background: `${riskBadgeColor}15`, color: riskBadgeColor, textTransform: 'capitalize',
          }}>{riskLevel}</span>
        </div>

        {/* Abuse Velocity */}
        <div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Abuse Velocity</div>
          <span style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600,
            background: `${velocityColors[velocityLevel] || '#6b7280'}15`,
            color: velocityColors[velocityLevel] || '#6b7280', textTransform: 'capitalize',
          }}>{d.abuse_velocity || 'None'}</span>
        </div>

        {/* Trusted Network */}
        {d.trusted_network && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '4px 12px', borderRadius: '12px',
            background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontSize: '13px', fontWeight: 500,
          }}>
            <CheckCircle2 size={14} />
            Trusted Network
          </div>
        )}

        {/* Privacy Service Detected (e.g., iCloud Private Relay) */}
        {d.privacy_service_detected && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '4px 12px', borderRadius: '12px',
            background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontSize: '13px', fontWeight: 500,
          }}>
            <Shield size={14} />
            {d.privacy_service_name || 'Privacy Service'}
          </div>
        )}

        {/* Connection Type */}
        {d.connection_type && (
          <div style={{ marginLeft: 'auto' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Connection</div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
              {d.connection_type}
            </div>
          </div>
        )}
      </div>

      {/* Privacy Service Notice */}
      {d.privacy_service_detected && (d.is_vpn || d.is_proxy) && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.08)',
          borderRadius: '12px',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          padding: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <Info size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, color: '#3b82f6', marginBottom: '4px' }}>
              Privacy Relay Detected
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              This connection appears to use <strong>{d.privacy_service_name || 'a privacy relay service'}</strong>,
              which is a legitimate privacy feature. The VPN/proxy flags are expected and the risk score has been adjusted accordingly.
            </div>
          </div>
        </div>
      )}

      {/* Section 2: Threat Assessment Grid */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <ShieldAlert size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Threat Assessment</span>
          <span style={{
            marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)',
          }}>{threats.filter(t => t.active).length} detected</span>
        </div>
        <div style={{ padding: '12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {threats.map(t => <ThreatBadge key={t.label} threat={t} />)}
        </div>
      </div>

      {/* Section 3: Network Details */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Wifi size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Network Details</span>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <InfoItem icon={Wifi} label="ISP" value={d.isp || '—'} />
            <InfoItem icon={Network} label="ASN" value={d.asn ? `AS${d.asn}` : '—'} />
            <InfoItem icon={Server} label="Organization" value={d.organization || '—'} />
            <InfoItem icon={Globe} label="Host" value={d.host || '—'} />
            <InfoItem icon={Radio} label="Shared Connection" value={d.shared_connection ? 'Yes' : 'No'} status={d.shared_connection ? 'warning' : null} />
            <InfoItem icon={Activity} label="Dynamic Connection" value={d.dynamic_connection ? 'Yes' : 'No'} />
          </div>
        </div>
      </div>

      {/* Section 4: Device & Browser */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <Monitor size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Device & Browser</span>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <InfoItem icon={Monitor} label="Operating System" value={d.operating_system || '—'} />
            <InfoItem icon={Globe} label="Browser" value={d.browser || '—'} />
            <InfoItem icon={Smartphone} label="Device Type" value={d.is_mobile ? 'Mobile' : 'Desktop'} />
            <InfoItem icon={Smartphone} label="Device Brand" value={d.device_brand || '—'} />
            <InfoItem icon={Smartphone} label="Device Model" value={d.device_model || '—'} />
            <InfoItem icon={Scan} label="Security Scanner" value={d.security_scanner ? 'Detected' : 'No'} status={d.security_scanner ? 'warning' : null} />
          </div>
        </div>
      </div>

      {/* Section 5: Geolocation */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <MapPin size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Geolocation</span>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <InfoItem icon={Globe} label="Country" value={d.country_code || '—'} />
            <InfoItem icon={MapPin} label="Region" value={d.region || '—'} />
            <InfoItem icon={MapPin} label="City" value={d.city || '—'} />
            <InfoItem icon={Navigation} label="Zip Code" value={d.zip_code || '—'} />
            <InfoItem icon={Clock} label="Timezone" value={d.timezone || '—'} />
            <InfoItem icon={Navigation} label="Coordinates" value={
              d.latitude && d.longitude ? `${d.latitude.toFixed(4)}, ${d.longitude.toFixed(4)}` : '—'
            } />
          </div>
        </div>
      </div>

      {/* Section 6: Abuse Intelligence */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <AlertCircle size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Abuse Intelligence</span>
        </div>
        <div style={{ padding: '16px' }}>
          {/* Abuse Velocity Bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>Abuse Velocity</div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {['none', 'low', 'medium', 'high'].map((level) => (
                <div key={level} style={{
                  flex: 1, height: '8px', borderRadius: '4px',
                  background: ['none', 'low', 'medium', 'high'].indexOf(velocityLevel) >= ['none', 'low', 'medium', 'high'].indexOf(level)
                    ? velocityColors[level] : 'var(--bg-tertiary)',
                  transition: 'background 0.2s',
                }} />
              ))}
              <span style={{ fontSize: '12px', fontWeight: 500, color: velocityColors[velocityLevel], marginLeft: '8px', textTransform: 'capitalize' }}>
                {d.abuse_velocity || 'None'}
              </span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <InfoItem icon={AlertTriangle} label="Frequent Abuser" value={d.frequent_abuser ? 'Yes' : 'No'} status={d.frequent_abuser ? 'warning' : 'success'} />
            <InfoItem icon={ShieldAlert} label="High Risk Attacks" value={d.high_risk_attacks ? 'Yes' : 'No'} status={d.high_risk_attacks ? 'warning' : 'success'} />
            <InfoItem icon={Scan} label="Security Scanner" value={d.security_scanner ? 'Detected' : 'No'} status={d.security_scanner ? 'warning' : null} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Info item helper component
const InfoItem = ({ icon: Icon, label, value, status }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
    <div style={{
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      background: 'var(--bg-tertiary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      <Icon size={14} style={{ color: 'var(--text-muted)' }} />
    </div>
    <div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>{label}</div>
      <div style={{
        fontSize: '13px',
        fontWeight: 500,
        color: status === 'warning' ? 'var(--warning)' : status === 'success' ? 'var(--success)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  </div>
);

// Signals section
const SignalsSection = ({ signals = {} }) => {
  const behaviorSignals = signals.behavior || [];
  const deviceSignals = signals.device || [];
  const networkSignals = signals.network || [];

  const SignalRow = ({ signal }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-color)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: signal.risk === 'high' ? '#ef4444' : signal.risk === 'medium' ? '#f59e0b' : '#10b981',
        }} />
        <span style={{ fontSize: '14px' }}>{signal.name}</span>
      </div>
      <span style={{
        fontSize: '12px',
        padding: '3px 8px',
        borderRadius: '4px',
        background: signal.risk === 'high' ? 'rgba(239, 68, 68, 0.15)' :
                   signal.risk === 'medium' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
        color: signal.risk === 'high' ? '#ef4444' : signal.risk === 'medium' ? '#f59e0b' : '#10b981',
        fontWeight: 500,
      }}>
        {signal.risk || 'Low'}
      </span>
    </div>
  );

  const EmptySignals = ({ label }) => (
    <div style={{ padding: '16px', textAlign: 'center' }}>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No {label} signals collected</div>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {/* Behavior Signals */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Activity size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Behavior Signals</span>
        </div>
        {behaviorSignals.length > 0 ? behaviorSignals.map((signal, idx) => (
          <SignalRow key={idx} signal={signal} />
        )) : <EmptySignals label="behavior" />}
      </div>

      {/* Device Signals */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Smartphone size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Device Signals</span>
        </div>
        {deviceSignals.length > 0 ? deviceSignals.map((signal, idx) => (
          <SignalRow key={idx} signal={signal} />
        )) : <EmptySignals label="device" />}
      </div>

      {/* Network Signals */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Wifi size={16} style={{ color: 'var(--accent-primary)' }} />
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Network Signals</span>
        </div>
        {networkSignals.length > 0 ? networkSignals.map((signal, idx) => (
          <SignalRow key={idx} signal={signal} />
        )) : <EmptySignals label="network" />}
      </div>
    </div>
  );
};

// Helper to format match type labels
const formatMatchType = (matchType) => {
  if (!matchType) return 'Unknown';
  return matchType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Main component
export default function BiometricVerificationPanel({
  applicant = {},
  documents = [],
  verificationResults = {},
  session = {},
  signals = {},
  reports = {},
  compliance = null,  // Unified compliance data from useComplianceSummary
  timing = {},
}) {
  const [activeTab, setActiveTab] = useState('overview');

  // Extract biometric data from verificationResults (no fake fallbacks)
  const biometricData = useMemo(() => {
    const verification = verificationResults || {};
    const raw = verification.raw || {};

    return {
      faceMatch: verification.face_match || null,
      liveness: verification.liveness || null,
      overallVerified: verification.verified === true,
      overallConfidence: verification.overall_confidence || null,
      processingTime: verification.processing_time_ms || null,
      verifiedAt: verification.verified_at || applicant.submitted_at || null,
      // Raw service results — keys match backend raw_service_results exactly
      faceQuality: raw.face_quality || null,
      ageEstimation: raw.age_estimation ? {
        min: raw.age_estimation.age_range_low ?? raw.age_estimation.min,
        max: raw.age_estimation.age_range_high ?? raw.age_estimation.max,
      } : null,
      addressVerification: raw.address_verify ? {
        verified: raw.address_verify.verification_status === 'verified',
        accuracy: raw.address_verify.confidence,
        deliverability: raw.address_verify.is_deliverable ? 'deliverable' : 'undeliverable',
        address_type: raw.address_verify.formatted,
        high_risk_area: raw.address_verify.is_high_risk_area,
        score: raw.address_verify.verification_score,
        coordinates: raw.address_verify.latitude ? {
          lat: raw.address_verify.latitude,
          lng: raw.address_verify.longitude,
        } : null,
      } : null,
      phoneCheck: raw.phone_check || null,
      emailCheck: raw.email_check || null,
      identityMatch: raw.identity_match || null,
      ipAddress: applicant.ip_address || raw.device_intel?.ip_address || null,
    };
  }, [applicant, verificationResults]);

  // Process documents
  const processedDocs = useMemo(() => {
    const result = {
      idFront: null,
      idBack: null,
      selfie: null,
    };

    documents.forEach(doc => {
      const type = (doc.type || '').toLowerCase();
      if (type.includes('selfie')) {
        result.selfie = doc;
      } else if (type.includes('back')) {
        result.idBack = doc;
      } else if (type.includes('passport') || type.includes('driver') || type.includes('license') || type.includes('id_card')) {
        result.idFront = doc;
      }
    });

    return result;
  }, [documents]);

  // Format time
  const formatTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className="biometric-verification-panel">
      <style>{`
        .biometric-verification-panel {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .bvp-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .bvp-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .bvp-header-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--accent-primary), var(--accent-tertiary));
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .bvp-header-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px;
        }

        .bvp-header-subtitle {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }

        .bvp-tabs {
          display: flex;
          gap: 4px;
          background: var(--bg-secondary);
          padding: 4px;
          border-radius: 10px;
          border: 1px solid var(--border-color);
          margin-bottom: 20px;
        }

        .bvp-tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s;
          border: none;
          background: transparent;
        }

        .bvp-tab:hover {
          color: var(--text-primary);
          background: var(--bg-hover);
        }

        .bvp-tab.active {
          color: var(--text-primary);
          background: var(--bg-tertiary);
        }

        .bvp-content {
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .bvp-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .bvp-summary-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 16px;
        }

        .bvp-summary-label {
          font-size: 12px;
          color: var(--text-muted);
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .bvp-summary-value {
          font-size: 24px;
          font-weight: 700;
        }

        .bvp-documents-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        @media (max-width: 1200px) {
          .bvp-summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .bvp-documents-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .bvp-summary-grid {
            grid-template-columns: 1fr;
          }
          .bvp-documents-grid {
            grid-template-columns: 1fr;
          }
          .bvp-tabs {
            flex-wrap: wrap;
          }
        }
      `}</style>

      {/* Header */}
      <div className="bvp-header">
        <div className="bvp-header-left">
          <div className="bvp-header-icon">
            <Scan size={24} />
          </div>
          <div>
            <h2 className="bvp-header-title">Biometric Verification</h2>
            <p className="bvp-header-subtitle">
              {biometricData.verifiedAt
                ? `Identity verified on ${new Date(biometricData.verifiedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : 'Verification not yet performed'
              }
            </p>
          </div>
        </div>
        <StatusBadge status={biometricData.overallConfidence !== null ? (biometricData.overallVerified ? 'passed' : 'failed') : 'pending'} />
      </div>

      {/* Tabs */}
      <div className="bvp-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`bvp-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="bvp-content">
        {activeTab === 'overview' && (
          <>
            {/* Summary Stats */}
            <div className="bvp-summary-grid">
              <div className="bvp-summary-card">
                <div className="bvp-summary-label">Overall Score</div>
                <div className="bvp-summary-value" style={{ color: biometricData.overallConfidence !== null ? (biometricData.overallConfidence >= 90 ? 'var(--success)' : 'var(--warning)') : 'var(--text-muted)' }}>
                  {biometricData.overallConfidence !== null ? `${biometricData.overallConfidence.toFixed(1)}%` : '---'}
                </div>
              </div>
              <div className="bvp-summary-card">
                <div className="bvp-summary-label">Face Similarity</div>
                <div className="bvp-summary-value" style={{ color: biometricData.faceMatch?.similarity >= 90 ? 'var(--success)' : biometricData.faceMatch ? 'var(--warning)' : 'var(--text-muted)' }}>
                  {biometricData.faceMatch?.similarity != null ? `${biometricData.faceMatch.similarity.toFixed(1)}%` : '---'}
                </div>
              </div>
              <div className="bvp-summary-card">
                <div className="bvp-summary-label">Liveness Score</div>
                <div className="bvp-summary-value" style={{ color: biometricData.liveness?.confidence >= 90 ? 'var(--success)' : biometricData.liveness ? 'var(--warning)' : 'var(--text-muted)' }}>
                  {biometricData.liveness?.confidence != null ? `${biometricData.liveness.confidence.toFixed(1)}%` : '---'}
                </div>
              </div>
              {biometricData.processingTime != null && (
                <div className="bvp-summary-card">
                  <div className="bvp-summary-label">Processing Time</div>
                  <div className="bvp-summary-value" style={{ color: 'var(--text-primary)' }}>
                    {formatTime(biometricData.processingTime)}
                  </div>
                </div>
              )}
            </div>

            {/* Face Comparison */}
            <div style={{ marginBottom: '24px' }}>
              <FaceComparisonView
                idPhoto={processedDocs.idFront?.url}
                selfie={processedDocs.selfie?.url}
                similarity={biometricData.faceMatch?.similarity}
                matched={biometricData.faceMatch?.match}
              />
            </div>

            {/* Liveness Analysis */}
            {biometricData.liveness && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Eye size={16} style={{ color: 'var(--accent-primary)' }} />
                  Liveness Analysis
                </h3>
                <div style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  overflow: 'hidden',
                }}>
                  <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    {biometricData.liveness.confidence != null && (
                      <InfoItem icon={Activity} label="Confidence" value={`${biometricData.liveness.confidence.toFixed(1)}%`} />
                    )}
                    {biometricData.liveness.anti_spoof_score != null && (
                      <InfoItem icon={Scan} label="Anti-Spoof Score" value={`${(biometricData.liveness.anti_spoof_score * 100).toFixed(1)}%`} />
                    )}
                    {biometricData.liveness.frames_analyzed != null && (
                      <InfoItem icon={Camera} label="Frames Analyzed" value={biometricData.liveness.frames_analyzed} />
                    )}
                    {biometricData.liveness.challenges_completed?.length > 0 && (
                      <InfoItem icon={CheckCircle2} label="Challenges" value={biometricData.liveness.challenges_completed.join(', ')} />
                    )}
                    {biometricData.liveness.verified_at && (
                      <InfoItem icon={Clock} label="Verified At" value={new Date(biometricData.liveness.verified_at).toLocaleString()} />
                    )}
                  </div>
                  {biometricData.liveness.device_info && (
                    <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Device Context
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                        <InfoItem icon={Smartphone} label="Platform" value={biometricData.liveness.device_info.platform || '—'} />
                        <InfoItem icon={Monitor} label="Screen" value={
                          biometricData.liveness.device_info.screen_width
                            ? `${biometricData.liveness.device_info.screen_width}x${biometricData.liveness.device_info.screen_height}`
                            : '—'
                        } />
                        <InfoItem icon={Globe} label="Timezone" value={biometricData.liveness.device_info.timezone || '—'} />
                      </div>
                    </div>
                  )}
                  {biometricData.liveness.frame_scores?.length > 0 && (
                    <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Frame-by-Frame Scores
                      </div>
                      <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '40px' }}>
                        {biometricData.liveness.frame_scores.map((score, idx) => (
                          <div
                            key={idx}
                            title={`Frame ${idx + 1}: ${(score * 100).toFixed(1)}%`}
                            style={{
                              flex: 1,
                              height: `${score * 100}%`,
                              background: score > 0.7 ? '#10b981' : score > 0.4 ? '#f59e0b' : '#ef4444',
                              borderRadius: '2px',
                              minWidth: '3px',
                              maxWidth: '12px',
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: 'var(--text-muted)' }}>
                        <span>Frame 1</span>
                        <span>Frame {biometricData.liveness.frame_scores.length}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Documents Grid */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={16} style={{ color: 'var(--accent-primary)' }} />
                Collected Documents
              </h3>
              <div className="bvp-documents-grid">
                <DocumentCard
                  title="Government ID"
                  side="Front"
                  imageUrl={processedDocs.idFront?.url}
                  status={processedDocs.idFront ? 'passed' : 'pending'}
                />
                {processedDocs.idBack && (
                  <DocumentCard
                    title="Government ID"
                    side="Back"
                    imageUrl={processedDocs.idBack?.url}
                    status="passed"
                  />
                )}
                <DocumentCard
                  title="Live Selfie"
                  imageUrl={processedDocs.selfie?.url}
                  status={processedDocs.selfie ? 'passed' : 'pending'}
                />
              </div>
            </div>

            {/* Quick Checks Summary */}
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle2 size={16} style={{ color: 'var(--accent-primary)' }} />
                Verification Summary
              </h3>
              <VerificationChecksTable checks={verificationResults.checks || []} />
            </div>

            {/* Identity Primitives: Address, Phone, Email */}
            {(biometricData.addressVerification || biometricData.phoneCheck || biometricData.emailCheck || biometricData.identityMatch) && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={16} style={{ color: 'var(--accent-primary)' }} />
                  Identity Primitives
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Address Verification */}
                  {biometricData.addressVerification && (
                    <div style={{
                      background: 'var(--bg-secondary)', borderRadius: '12px',
                      border: '1px solid var(--border-color)', overflow: 'hidden',
                    }}>
                      <div style={{
                        padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPin size={16} style={{ color: 'var(--accent-primary)' }} />
                          <span style={{ fontSize: '14px', fontWeight: 600 }}>Address Verification</span>
                        </div>
                        <StatusBadge status={biometricData.addressVerification.verified ? 'passed' : 'failed'} size="sm" />
                      </div>
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                          <InfoItem icon={MapPin} label="Accuracy" value={biometricData.addressVerification.accuracy || '\u2014'} />
                          <InfoItem icon={CheckCircle2} label="Deliverability" value={biometricData.addressVerification.deliverability || '\u2014'}
                            status={biometricData.addressVerification.deliverability === 'deliverable' ? 'success' : 'warning'} />
                          <InfoItem icon={Globe} label="Address Type" value={biometricData.addressVerification.address_type || '\u2014'} />
                          {biometricData.addressVerification.high_risk_area && (
                            <InfoItem icon={AlertTriangle} label="High-Risk Area" value="Yes" status="warning" />
                          )}
                          {biometricData.addressVerification.score != null && (
                            <InfoItem icon={Activity} label="Score" value={`${biometricData.addressVerification.score}/100`} />
                          )}
                          {biometricData.addressVerification.coordinates && (
                            <InfoItem icon={Navigation} label="Coordinates" value={
                              `${biometricData.addressVerification.coordinates.lat?.toFixed(4)}, ${biometricData.addressVerification.coordinates.lng?.toFixed(4)}`
                            } />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Phone + Email row */}
                  {(biometricData.phoneCheck || biometricData.emailCheck) && (
                    <div style={{ display: 'grid', gridTemplateColumns: biometricData.phoneCheck && biometricData.emailCheck ? '1fr 1fr' : '1fr', gap: '16px' }}>
                      {/* Phone Check */}
                      {biometricData.phoneCheck && (
                        <div style={{
                          background: 'var(--bg-secondary)', borderRadius: '12px',
                          border: '1px solid var(--border-color)', overflow: 'hidden',
                        }}>
                          <div style={{
                            padding: '12px 16px', borderBottom: '1px solid var(--border-color)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Smartphone size={14} style={{ color: 'var(--accent-primary)' }} />
                              <span style={{ fontSize: '13px', fontWeight: 600 }}>Phone Check</span>
                            </div>
                            <StatusBadge status={biometricData.phoneCheck.valid ? 'passed' : 'failed'} size="sm" />
                          </div>
                          <div style={{ padding: '12px 16px' }}>
                            {/* Show failure reason if phone check failed */}
                            {!biometricData.phoneCheck.valid && biometricData.phoneCheck.user_message && (
                              <div style={{
                                padding: '10px 12px',
                                marginBottom: '12px',
                                background: 'rgba(239, 68, 68, 0.08)',
                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                borderRadius: '8px',
                                fontSize: '13px',
                                color: '#ef4444',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                              }}>
                                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span>{biometricData.phoneCheck.user_message}</span>
                              </div>
                            )}
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {[
                                { label: 'Line Type', value: biometricData.phoneCheck.line_type, highlight: biometricData.phoneCheck.line_type?.toLowerCase() === 'voip' },
                                { label: 'Carrier', value: biometricData.phoneCheck.carrier },
                                { label: 'Fraud Score', value: biometricData.phoneCheck.fraud_score != null ? `${biometricData.phoneCheck.fraud_score}/100` : null, highlight: biometricData.phoneCheck.fraud_score >= 75 },
                                { label: 'Active', value: biometricData.phoneCheck.active != null ? (biometricData.phoneCheck.active ? 'Yes' : 'No') : null },
                              ].filter(r => r.value).map((row, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                  <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                                  <span style={{ fontWeight: 500, color: row.highlight ? '#ef4444' : 'var(--text-primary)' }}>{row.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Email Check */}
                      {biometricData.emailCheck && (
                        <div style={{
                          background: 'var(--bg-secondary)', borderRadius: '12px',
                          border: '1px solid var(--border-color)', overflow: 'hidden',
                        }}>
                          <div style={{
                            padding: '12px 16px', borderBottom: '1px solid var(--border-color)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Globe size={14} style={{ color: 'var(--accent-primary)' }} />
                              <span style={{ fontSize: '13px', fontWeight: 600 }}>Email Check</span>
                            </div>
                            <StatusBadge status={biometricData.emailCheck.valid ? 'passed' : 'failed'} size="sm" />
                          </div>
                          <div style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {[
                                { label: 'Disposable', value: biometricData.emailCheck.disposable != null ? (biometricData.emailCheck.disposable ? 'Yes' : 'No') : null },
                                { label: 'Fraud Score', value: biometricData.emailCheck.fraud_score != null ? `${biometricData.emailCheck.fraud_score}/100` : null },
                                { label: 'Deliverability', value: biometricData.emailCheck.deliverability },
                                { label: 'Domain Age', value: biometricData.emailCheck.domain_age_days != null ? `${biometricData.emailCheck.domain_age_days} days` : null },
                              ].filter(r => r.value).map((row, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                  <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                                  <span style={{ fontWeight: 500, color: row.label === 'Disposable' && row.value === 'Yes' ? '#ef4444' : 'var(--text-primary)' }}>
                                    {row.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Identity Match */}
                  {biometricData.identityMatch && (
                    <div style={{
                      background: 'var(--bg-secondary)', borderRadius: '12px',
                      border: '1px solid var(--border-color)', overflow: 'hidden',
                    }}>
                      <div style={{
                        padding: '12px 16px', borderBottom: '1px solid var(--border-color)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <User size={14} style={{ color: 'var(--accent-primary)' }} />
                          <span style={{ fontSize: '13px', fontWeight: 600 }}>Identity Match</span>
                        </div>
                        {biometricData.identityMatch.summary_score != null && (
                          <span style={{
                            fontSize: '13px', fontWeight: 700,
                            color: biometricData.identityMatch.summary_score >= 80 ? '#10b981' :
                                   biometricData.identityMatch.summary_score >= 50 ? '#f59e0b' : '#ef4444',
                          }}>
                            {biometricData.identityMatch.summary_score}%
                          </span>
                        )}
                      </div>
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                          {[
                            { label: 'First Name', value: biometricData.identityMatch.first_name_match },
                            { label: 'Last Name', value: biometricData.identityMatch.last_name_match },
                            { label: 'DOB Match', value: biometricData.identityMatch.dob_match },
                            { label: 'Address Match', value: biometricData.identityMatch.address_match },
                            { label: 'Phone Match', value: biometricData.identityMatch.phone_match },
                            { label: 'Email Match', value: biometricData.identityMatch.email_match },
                          ].filter(r => r.value != null).map((row, i) => {
                            // Format the display value
                            let displayValue;
                            let color;
                            if (typeof row.value === 'boolean') {
                              displayValue = row.value ? 'Match' : 'No Match';
                              color = row.value ? '#10b981' : '#ef4444';
                            } else if (typeof row.value === 'number') {
                              displayValue = `${row.value}%`;
                              color = row.value >= 80 ? '#10b981' : row.value >= 50 ? '#f59e0b' : '#ef4444';
                            } else if (typeof row.value === 'string') {
                              // Format string values like "high_partial_match" -> "High Partial Match"
                              displayValue = formatMatchType(row.value);
                              // Determine color based on match type
                              const lowerValue = row.value.toLowerCase();
                              if (lowerValue.includes('exact') || lowerValue === 'match') {
                                color = '#10b981';
                              } else if (lowerValue.includes('partial') || lowerValue.includes('high')) {
                                color = '#f59e0b';
                              } else if (lowerValue.includes('no_match') || lowerValue.includes('mismatch')) {
                                color = '#ef4444';
                              } else {
                                color = '#f59e0b';
                              }
                            } else {
                              displayValue = String(row.value);
                              color = 'var(--text-primary)';
                            }

                            return (
                              <div key={i} style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>{row.label}</div>
                                <div style={{
                                  fontSize: '14px', fontWeight: 600,
                                  color: color,
                                }}>
                                  {displayValue}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'verifications' && (
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* ====== SELFIE VERIFICATION SECTION ====== */}
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Camera size={18} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '16px', fontWeight: 600 }}>Selfie Verification</span>
                </div>
                <StatusBadge status={
                  biometricData.liveness?.is_live && biometricData.faceMatch?.match ? 'passed' :
                  biometricData.liveness || biometricData.faceMatch ? 'failed' : 'pending'
                } />
              </div>

              <div style={{ padding: '20px' }}>
                {/* Selfie image + capture method */}
                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                  {processedDocs.selfie?.url && (
                    <div style={{
                      width: '120px', height: '150px', borderRadius: '12px', overflow: 'hidden',
                      border: '2px solid var(--border-color)', background: '#1a1a2e', flexShrink: 0,
                    }}>
                      <FaceImage src={processedDocs.selfie.url} alt="Selfie" icon={Camera} />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Capture Method
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
                      {biometricData.liveness?.provider === 'mediapipe' ? 'Active Liveness (Challenge-Based)' :
                       biometricData.liveness?.provider === 'passive_onnx' ? 'Passive Liveness (Anti-Spoof)' :
                       biometricData.liveness?.provider || 'Live Autocapture'}
                    </div>
                    {biometricData.liveness?.challenges_completed?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Challenges Completed</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {biometricData.liveness.challenges_completed.map((c, i) => (
                            <span key={i} style={{
                              fontSize: '11px', padding: '3px 8px', borderRadius: '4px',
                              background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 500,
                            }}>{c.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selfie checks table */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Checks
                  </div>
                  {(() => {
                    const fq = biometricData.faceQuality || {};
                    const selfieChecks = [
                      {
                        name: 'Selfie-to-ID Comparison',
                        status: biometricData.faceMatch?.match === true ? 'passed' : biometricData.faceMatch?.match === false ? 'failed' : null,
                        value: biometricData.faceMatch?.similarity != null ? `${biometricData.faceMatch.similarity.toFixed(1)}%` : null,
                      },
                      {
                        name: 'Liveness Detection',
                        status: biometricData.liveness?.is_live === true ? 'passed' : biometricData.liveness?.is_live === false ? 'failed' : null,
                        value: biometricData.liveness?.confidence != null ? `${biometricData.liveness.confidence.toFixed(0)}%` : null,
                      },
                      {
                        name: 'Anti-Spoof Score',
                        status: biometricData.liveness?.anti_spoof_score != null ? (biometricData.liveness.anti_spoof_score > 0.5 ? 'passed' : 'failed') : null,
                        value: biometricData.liveness?.anti_spoof_score != null ? `${(biometricData.liveness.anti_spoof_score * 100).toFixed(1)}%` : null,
                      },
                      {
                        name: 'Portrait Quality',
                        status: fq.is_acceptable === true ? 'passed' : fq.is_acceptable === false ? 'failed' : null,
                        value: null,
                      },
                      {
                        name: 'Eyes Open',
                        status: fq.eyes_open === true ? 'passed' : fq.eyes_open === false ? 'failed' : null,
                        value: null,
                      },
                      {
                        name: 'Acceptable Pose',
                        status: fq.pose_yaw != null ? (Math.abs(fq.pose_yaw) < 30 && Math.abs(fq.pose_pitch || 0) < 30 ? 'passed' : 'failed') : null,
                        value: fq.pose_yaw != null ? `Yaw: ${fq.pose_yaw.toFixed(0)}° Pitch: ${(fq.pose_pitch || 0).toFixed(0)}°` : null,
                      },
                      {
                        name: 'Image Brightness',
                        status: fq.brightness != null ? (fq.brightness > 40 ? 'passed' : 'failed') : null,
                        value: fq.brightness != null ? `${fq.brightness.toFixed(0)}` : null,
                      },
                      {
                        name: 'Image Sharpness',
                        status: fq.sharpness != null ? (fq.sharpness > 50 ? 'passed' : 'failed') : null,
                        value: fq.sharpness != null ? `${fq.sharpness.toFixed(0)}` : null,
                      },
                      {
                        name: 'Multiple Faces',
                        status: fq.faces_detected != null ? (fq.faces_detected === 1 ? 'passed' : 'failed') : null,
                        value: fq.faces_detected != null ? `${fq.faces_detected} detected` : null,
                      },
                      {
                        name: 'Age Consistency',
                        status: biometricData.ageEstimation ? (() => {
                          const dob = applicant.date_of_birth;
                          if (!dob) return null;
                          const age = Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
                          const est = biometricData.ageEstimation;
                          return (age >= (est.min || 0) - 10 && age <= (est.max || 100) + 10) ? 'passed' : 'failed';
                        })() : null,
                        value: biometricData.ageEstimation ? `${biometricData.ageEstimation.min || '?'}-${biometricData.ageEstimation.max || '?'} years` : null,
                      },
                    ].filter(c => c.status !== null);

                    if (selfieChecks.length === 0) {
                      return (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                          No selfie verification data available
                        </div>
                      );
                    }

                    return selfieChecks.map((check, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 0',
                        borderBottom: idx < selfieChecks.length - 1 ? '1px solid var(--border-color)' : 'none',
                      }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 500 }}>{check.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            {CHECK_EXPLANATIONS[check.name] || ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {check.value && (
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>
                              {check.value}
                            </span>
                          )}
                          <StatusBadge status={check.status} size="sm" />
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Frame Score Analysis */}
                {biometricData.liveness?.frame_scores?.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Frame Score Analysis
                    </div>
                    <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '48px', marginBottom: '8px' }}>
                      {biometricData.liveness.frame_scores.map((score, idx) => (
                        <div
                          key={idx}
                          title={`Frame ${idx + 1}: ${(score * 100).toFixed(1)}%`}
                          style={{
                            flex: 1, height: `${score * 100}%`,
                            background: score > 0.7 ? '#10b981' : score > 0.4 ? '#f59e0b' : '#ef4444',
                            borderRadius: '2px', minWidth: '3px', maxWidth: '12px',
                          }}
                        />
                      ))}
                    </div>
                    {/* Stats row */}
                    {(() => {
                      const scores = biometricData.liveness.frame_scores;
                      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
                      const min = Math.min(...scores);
                      const max = Math.max(...scores);
                      const stdDev = Math.sqrt(scores.reduce((s, v) => s + (v - avg) ** 2, 0) / scores.length);
                      return (
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          {[
                            { label: 'Avg', value: `${(avg * 100).toFixed(1)}%` },
                            { label: 'Min', value: `${(min * 100).toFixed(1)}%` },
                            { label: 'Max', value: `${(max * 100).toFixed(1)}%` },
                            { label: 'Std Dev', value: `${(stdDev * 100).toFixed(2)}%` },
                            { label: 'Threshold', value: '50%' },
                          ].map(s => (
                            <div key={s.label} style={{ fontSize: '12px' }}>
                              <span style={{ color: 'var(--text-muted)' }}>{s.label}: </span>
                              <span style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Device Context */}
                {biometricData.liveness?.device_info && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Device Context
                    </div>
                    {(() => {
                      const di = biometricData.liveness.device_info;
                      const parsed = parseUserAgent(di.user_agent);
                      return (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                          <InfoItem icon={Globe} label="Browser" value={parsed.browser} />
                          <InfoItem icon={Monitor} label="OS" value={parsed.os} />
                          <InfoItem icon={Smartphone} label="Device" value={parsed.device !== '\u2014' ? parsed.device : (di.platform || '\u2014')} />
                          <InfoItem icon={Monitor} label="Screen" value={
                            di.screen_width ? `${di.screen_width}\u00d7${di.screen_height}` : '\u2014'
                          } />
                          <InfoItem icon={Eye} label="Pixel Ratio" value={di.pixel_ratio || '\u2014'} />
                          <InfoItem icon={Clock} label="Timezone" value={di.timezone || '\u2014'} />
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* ====== DOCUMENT VERIFICATION SECTION (per document) ====== */}
            {documents.filter(d => !(d.type || '').toLowerCase().includes('selfie')).map((doc, docIdx) => (
              <div key={doc.id || docIdx} style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FileText size={18} style={{ color: 'var(--accent-primary)' }} />
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>
                      Document Verification
                      {doc.type && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '8px', fontSize: '13px' }}>
                        ({doc.type.replace(/_/g, ' ')})
                      </span>}
                    </span>
                  </div>
                  <StatusBadge status={
                    (doc.fraud_signals || []).some(s => s.severity === 'high') ? 'failed' :
                    doc.status === 'processed' || doc.verification_checks?.length > 0 ? 'passed' : 'pending'
                  } />
                </div>

                <div style={{ padding: '20px' }}>
                  {/* Document image + extracted attributes */}
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                    {doc.url && (
                      <div style={{
                        width: '180px', height: '120px', borderRadius: '12px', overflow: 'hidden',
                        border: '2px solid var(--border-color)', background: '#1a1a2e', flexShrink: 0,
                      }}>
                        <img src={doc.url} alt={doc.type} onError={(e) => { e.target.style.display = 'none'; }}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px' }} />
                      </div>
                    )}
                    {/* Extracted Attributes Grid */}
                    {doc.extracted_data && Object.keys(doc.extracted_data).length > 0 && (
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Extracted Attributes
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {[
                            { label: 'Full Name', key: 'full_name', alt: ['name', 'first_name'] },
                            { label: 'Date of Birth', key: 'date_of_birth', alt: ['dob'] },
                            { label: 'Document Number', key: 'document_number', alt: ['id_number'] },
                            { label: 'Nationality', key: 'nationality', alt: ['country'] },
                            { label: 'Sex', key: 'sex', alt: ['gender'] },
                            { label: 'Issue Date', key: 'issue_date' },
                            { label: 'Expiry Date', key: 'expiry_date', alt: ['expiration_date'] },
                            { label: 'Issuing Country', key: 'issuing_country' },
                          ].map(field => {
                            const val = doc.extracted_data[field.key] ||
                              (field.alt || []).reduce((v, k) => v || doc.extracted_data[k], null);
                            if (!val) return null;
                            return (
                              <div key={field.key} style={{ fontSize: '13px' }}>
                                <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '2px' }}>{field.label}</div>
                                <div style={{ fontWeight: 500 }}>{val}</div>
                              </div>
                            );
                          }).filter(Boolean)}
                        </div>
                        {doc.extracted_data.mrz && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '2px' }}>MRZ</div>
                            <div style={{
                              fontFamily: "'JetBrains Mono', monospace", fontSize: '11px',
                              background: 'var(--bg-tertiary)', padding: '6px 8px', borderRadius: '4px',
                              wordBreak: 'break-all',
                            }}>{doc.extracted_data.mrz}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* OCR Confidence Meter */}
                  {doc.ocr_confidence != null && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>OCR Confidence</span>
                        <span style={{ fontWeight: 600, color: doc.ocr_confidence >= 90 ? '#10b981' : doc.ocr_confidence >= 70 ? '#f59e0b' : '#ef4444' }}>
                          {doc.ocr_confidence.toFixed(1)}%
                        </span>
                      </div>
                      <div style={{ height: '6px', background: 'var(--bg-tertiary)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${doc.ocr_confidence}%`, borderRadius: '3px',
                          background: doc.ocr_confidence >= 90 ? '#10b981' : doc.ocr_confidence >= 70 ? '#f59e0b' : '#ef4444',
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Document Checks */}
                  {(() => {
                    const docChecks = [];
                    // From verification_checks array
                    if (doc.verification_checks?.length > 0) {
                      doc.verification_checks.forEach(vc => {
                        docChecks.push({
                          name: vc.check || vc.name || 'Unknown Check',
                          status: vc.passed ? 'passed' : 'failed',
                          value: vc.confidence != null ? `${vc.confidence.toFixed(0)}%` : null,
                          type: vc.type || 'fraud',
                        });
                      });
                    }
                    // Document expiry check
                    if (doc.extracted_data?.expiry_date) {
                      const expired = new Date(doc.extracted_data.expiry_date) < new Date();
                      docChecks.push({ name: 'Not Expired', status: expired ? 'failed' : 'passed', value: doc.extracted_data.expiry_date, type: 'fraud' });
                    }
                    // MRZ check
                    if (doc.extracted_data?.mrz) {
                      docChecks.push({ name: 'MRZ Validated', status: 'passed', value: null, type: 'fraud' });
                    }
                    // Name consistency
                    if (doc.extracted_data?.full_name && applicant.first_name) {
                      const docName = (doc.extracted_data.full_name || '').toLowerCase();
                      const appName = `${applicant.first_name} ${applicant.last_name || ''}`.toLowerCase().trim();
                      const match = docName.includes(appName.split(' ')[0]) || appName.includes(docName.split(' ')[0]);
                      docChecks.push({ name: 'Name Consistency', status: match ? 'passed' : 'failed', value: doc.extracted_data.full_name, type: 'fraud' });
                    }

                    if (docChecks.length === 0) return null;

                    return (
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Document Checks
                        </div>
                        {docChecks.map((check, idx) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 0',
                            borderBottom: idx < docChecks.length - 1 ? '1px solid var(--border-color)' : 'none',
                          }}>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 500 }}>{check.name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                {CHECK_EXPLANATIONS[check.name] || ''}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {check.value && (
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>
                                  {check.value}
                                </span>
                              )}
                              <span style={{
                                fontSize: '11px', padding: '2px 6px', borderRadius: '4px',
                                background: check.type === 'fraud' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                color: check.type === 'fraud' ? '#ef4444' : '#3b82f6', fontWeight: 500,
                              }}>
                                {check.type === 'fraud' ? 'Fraud' : 'Behavior'}
                              </span>
                              <StatusBadge status={check.status} size="sm" />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Security Features */}
                  {doc.security_features_detected?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Security Features Detected
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {doc.security_features_detected.map((feature, idx) => (
                          <span key={idx} style={{
                            fontSize: '12px', padding: '4px 10px', borderRadius: '6px',
                            background: 'rgba(16, 185, 129, 0.1)', color: '#10b981',
                            fontWeight: 500, textTransform: 'capitalize',
                          }}>
                            {feature.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fraud Signals */}
                  {doc.fraud_signals?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Fraud Signals
                      </div>
                      {doc.fraud_signals.map((signal, idx) => (
                        <div key={idx} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: '8px', marginBottom: '6px',
                          background: signal.severity === 'high' ? 'rgba(239, 68, 68, 0.08)' :
                                     signal.severity === 'medium' ? 'rgba(245, 158, 11, 0.08)' : 'var(--bg-tertiary)',
                          border: `1px solid ${signal.severity === 'high' ? 'rgba(239, 68, 68, 0.2)' :
                                               signal.severity === 'medium' ? 'rgba(245, 158, 11, 0.2)' : 'var(--border-color)'}`,
                        }}>
                          <AlertTriangle size={14} style={{
                            color: signal.severity === 'high' ? '#ef4444' : signal.severity === 'medium' ? '#f59e0b' : 'var(--text-muted)',
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500 }}>{signal.signal || signal.name}</div>
                            {signal.details && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{signal.details}</div>}
                          </div>
                          <span style={{
                            fontSize: '11px', padding: '2px 8px', borderRadius: '4px', fontWeight: 600, textTransform: 'uppercase',
                            background: signal.severity === 'high' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                            color: signal.severity === 'high' ? '#ef4444' : '#f59e0b',
                          }}>
                            {signal.severity}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Fallback: existing checks table if no structured data */}
            {documents.filter(d => !(d.type || '').toLowerCase().includes('selfie')).length === 0 &&
             !biometricData.liveness && !biometricData.faceMatch && (
              <VerificationChecksTable checks={verificationResults.checks || []} />
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Session Timing */}
            {(timing.created_at || timing.submitted_at || timing.verified_at) && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <Clock size={16} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Session Timing</span>
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <InfoItem icon={Clock} label="Created At" value={
                      timing.created_at ? new Date(timing.created_at).toLocaleString() : '\u2014'
                    } />
                    <InfoItem icon={Clock} label="Submitted At" value={
                      timing.submitted_at ? new Date(timing.submitted_at).toLocaleString() : '\u2014'
                    } />
                    <InfoItem icon={CheckCircle2} label="Verified At" value={
                      timing.verified_at ? new Date(timing.verified_at).toLocaleString() : '\u2014'
                    } />
                  </div>
                  {timing.created_at && timing.verified_at && (
                    <div style={{ marginTop: '12px', padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: '8px', fontSize: '13px' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Total Duration: </span>
                      <span style={{ fontWeight: 600 }}>
                        {(() => {
                          const ms = new Date(timing.verified_at) - new Date(timing.created_at);
                          if (ms < 60000) return `${Math.round(ms / 1000)}s`;
                          if (ms < 3600000) return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
                          return `${Math.round(ms / 3600000)}h ${Math.round((ms % 3600000) / 60000)}m`;
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* IP Address */}
            {biometricData.ipAddress && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <Globe size={16} style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>IP Address</div>
                  <div style={{ fontSize: '14px', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>
                    {biometricData.ipAddress}
                  </div>
                </div>
              </div>
            )}

            {/* Parsed User Agent */}
            {biometricData.liveness?.device_info?.user_agent && (
              <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <Monitor size={16} style={{ color: 'var(--accent-primary)' }} />
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>User Agent</span>
                </div>
                <div style={{ padding: '16px' }}>
                  {(() => {
                    const parsed = parseUserAgent(biometricData.liveness.device_info.user_agent);
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                          <InfoItem icon={Globe} label="Browser" value={parsed.browser} />
                          <InfoItem icon={Monitor} label="Operating System" value={parsed.os} />
                          <InfoItem icon={Smartphone} label="Device" value={parsed.device} />
                        </div>
                        <div style={{
                          padding: '8px 12px', background: 'var(--bg-tertiary)', borderRadius: '6px',
                          fontSize: '11px', fontFamily: "'JetBrains Mono', monospace",
                          color: 'var(--text-muted)', wordBreak: 'break-all', lineHeight: 1.5,
                        }}>
                          {parsed.full}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Existing device intel session info */}
            <SessionInfo session={session} deviceIntel={verificationResults.device_intel} />
          </div>
        )}

        {activeTab === 'signals' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Featured Signals */}
            {(() => {
              const deviceIntel = verificationResults.device_intel || {};
              const featuredSignals = [
                { name: 'Behavior Threat Level', value: deviceIntel.risk_level || 'Unknown', type: 'Processed',
                  risk: deviceIntel.risk_level === 'high' ? 'high' : deviceIntel.risk_level === 'medium' ? 'medium' : 'low' },
                { name: 'Network Threat Level', value: deviceIntel.fraud_score != null ? `${deviceIntel.fraud_score}/100` : 'Unknown', type: 'Processed',
                  risk: (deviceIntel.fraud_score || 0) >= 75 ? 'high' : (deviceIntel.fraud_score || 0) >= 40 ? 'medium' : 'low' },
                { name: 'Proxy Detected', value: deviceIntel.is_proxy ? 'Yes' : 'No', type: 'Raw',
                  risk: deviceIntel.is_proxy ? 'high' : 'low' },
                { name: 'VPN Detected', value: deviceIntel.is_vpn ? 'Yes' : 'No', type: 'Raw',
                  risk: deviceIntel.is_vpn ? 'medium' : 'low' },
                { name: 'Abuse Velocity', value: deviceIntel.abuse_velocity || 'None', type: 'Processed',
                  risk: (deviceIntel.abuse_velocity || '').toLowerCase() === 'high' ? 'high' :
                        (deviceIntel.abuse_velocity || '').toLowerCase() === 'medium' ? 'medium' : 'low' },
                { name: 'Bot Detected', value: deviceIntel.is_bot ? 'Yes' : 'No', type: 'Raw',
                  risk: deviceIntel.is_bot ? 'high' : 'low' },
              ].filter(s => s.value !== 'Unknown' && s.value !== undefined);

              if (featuredSignals.length === 0) return null;

              return (
                <div style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '14px 16px', borderBottom: '1px solid var(--border-color)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    <ShieldAlert size={16} style={{ color: 'var(--accent-primary)' }} />
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>Featured Signals</span>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {featuredSignals.filter(s => s.risk === 'high').length} high risk
                    </span>
                  </div>
                  {/* Header row */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 120px 80px',
                    padding: '10px 16px', background: 'var(--bg-tertiary)',
                    fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: '0.05em', color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border-color)',
                  }}>
                    <span>Signal</span>
                    <span>Value</span>
                    <span style={{ textAlign: 'right' }}>Type</span>
                  </div>
                  {featuredSignals.map((signal, idx) => (
                    <div key={idx} style={{
                      display: 'grid', gridTemplateColumns: '1fr 120px 80px',
                      padding: '12px 16px', alignItems: 'center',
                      borderBottom: idx < featuredSignals.length - 1 ? '1px solid var(--border-color)' : 'none',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: signal.risk === 'high' ? '#ef4444' : signal.risk === 'medium' ? '#f59e0b' : '#10b981',
                        }} />
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 500 }}>{signal.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
                            {CHECK_EXPLANATIONS[signal.name] || ''}
                          </div>
                        </div>
                      </div>
                      <span style={{
                        fontSize: '13px', fontWeight: 600, textTransform: 'capitalize',
                        color: signal.risk === 'high' ? '#ef4444' : signal.risk === 'medium' ? '#f59e0b' : 'var(--text-primary)',
                      }}>
                        {signal.value}
                      </span>
                      <span style={{
                        fontSize: '11px', padding: '2px 6px', borderRadius: '4px', textAlign: 'center',
                        background: signal.type === 'Processed' ? 'var(--accent-glow-medium)' : 'rgba(156, 163, 175, 0.1)',
                        color: signal.type === 'Processed' ? 'var(--accent-primary)' : 'var(--text-muted)',
                        fontWeight: 500,
                      }}>
                        {signal.type}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Existing categorized signals */}
            <SignalsSection signals={signals} />
          </div>
        )}
      </div>
    </div>
  );
}
